import React from "react";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { Button } from "@/components/ui/button";
import { QRBadge } from "../components/QRBadge";
import { Seo } from "../components/Seo";
import { ExitIntentPopup } from "../components/ExitIntentPopup";
import { OrganizationSchema, WirelessOffersSchema, FaqSchema, WIRELESS_FAQ } from "../components/StructuredData";
import { MessageSquare, Globe, Zap, ArrowUpRight, Check, Plane, Tag, Building2, ArrowRight } from "lucide-react";
import { SiteNav } from "../components/SiteNav";
import { useScanTracking } from "../lib/scanTracking";
import { SiteFooter } from "../components/SiteFooter";
import { HERO_IMG, WirelessPlanCard, useChoosePlan } from "./Wireless";



const tickerNumbers = [
    "+1 (415) 555 0188",
    "+44 20 7946 0827",
    "+1 (212) 555 0241",
    "+1 (305) 555 0118",
    "+1 (646) 555 0173",
    "+49 30 12345678",
    "+1 (917) 555 0192",
    "+1 (510) 555 0144",
];

const useCases = [
    { icon: MessageSquare, label: "Unlimited talk & text" },
    { icon: Zap, label: "Fast 5G / LTE data" },
    { icon: Globe, label: "Mobile hotspot" },
    { icon: Plane, label: "International & roaming" },
];

export default function Landing() {
    const { user, startLogin } = useAuth();
    const navigate = useNavigate();
    const [mobilePlans, setMobilePlans] = React.useState([]);
    const [mobileFees, setMobileFees] = React.useState({ regulatory_monthly: 3.99, activation_one_time: 15, data_per_gb: 15 });
    const choosePlan = useChoosePlan();
    useScanTracking("/");

    React.useEffect(() => {
        api.get("/mobile/plans").then((r) => { setMobilePlans(r.data.plans || []); if (r.data.fees) setMobileFees(r.data.fees); }).catch(() => {});
    }, []);

    // Capture referral code from URL into localStorage so OAuth flow can pick it up
    React.useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const ref = params.get("ref");
        if (ref) {
            localStorage.setItem("flex_ref_code", ref.toUpperCase().slice(0, 16));
        }
        const fr = params.get("fr");
        if (fr) {
            localStorage.setItem("flex_fr_code", fr.toUpperCase().slice(0, 16));
        }
    }, []);

    const onCTA = () => (user ? navigate("/dashboard") : startLogin());

    return (
        <div className="min-h-screen bg-cream text-zinc-900">
            <ExitIntentPopup />
            <OrganizationSchema />
            <WirelessOffersSchema plans={mobilePlans} />
            <FaqSchema items={WIRELESS_FAQ} />
            <Seo
                title="FlexLine Mobile — Wireless eSIM Plans from $19.99 · No Contract"
                description="FlexLine Mobile is wireless, reimagined — eSIM data plans with unlimited talk & text, keep your existing number with instant porting, plus optional second phone numbers. No store visit, activate in minutes."
                path="/"
            />
            {/* Nav */}
            <SiteNav />

            {/* Promo announcement bar */}
            <div className="bg-[#D97706] text-white text-center py-2.5 px-4" data-testid="promo-bar">
                <p className="font-mono text-[11px] sm:text-xs uppercase tracking-wider">
                    Wireless plans from <span className="font-bold">$19.99/mo</span> — activate your eSIM in minutes. No contract.
                </p>
            </div>

            {/* Hero */}
            <section className="relative overflow-hidden border-b border-[#DDD6FE]">
                <div className="absolute inset-0 grid-bg pointer-events-none opacity-60" />
                <div className="relative max-w-7xl mx-auto px-6 lg:px-12 pt-20 pb-28 md:pt-24 md:pb-32">
                    <div className="grid lg:grid-cols-12 gap-12 items-start">
                        <div className="lg:col-span-7">
                            <div className="inline-flex items-center gap-2 px-3 py-1.5 border border-[#DDD6FE] rounded-sm bg-paper mb-8">
                                <span className="ticker-dot" />
                                <span className="font-mono text-[11px] uppercase tracking-wider text-zinc-700">
                                    eSIM wireless · Unlimited talk &amp; text · Keep your number
                                </span>
                            </div>
                            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[0.95] tracking-tight font-light">
                                Wireless,
                                <br />
                                <span className="font-serif-italic font-normal text-[#4C1D95]">reimagined.</span>
                                <br />
                                <span className="font-light text-emerald-deep">No store. No SIM.</span>
                            </h1>
                            <p className="mt-8 text-base md:text-lg text-zinc-700 max-w-xl leading-relaxed">
                                FlexLine Mobile is a full wireless carrier on eSIM — unlimited talk &amp; text and 5G/LTE data,
                                paid upfront and activated automatically in minutes. Bring your number or get a new one.
                            </p>
                            <div className="mt-10 flex flex-col sm:flex-row gap-3">
                                <Button
                                    data-testid="hero-wireless-btn"
                                    onClick={() => navigate("/wireless")}
                                    className="bg-[#4C1D95] text-white hover:bg-[#2E1065] rounded-full h-12 px-7 text-base font-medium"
                                >
                                    See wireless plans <ArrowUpRight className="w-4 h-4 ml-2" />
                                </Button>
                                <Button
                                    data-testid="hero-get-started-btn"
                                    variant="outline"
                                    onClick={() => navigate("/second-number")}
                                    className="border-[#D97706] text-[#B45309] bg-transparent hover:bg-[#FEF3C7] rounded-full h-12 px-7 text-base"
                                >
                                    Need just a second number?
                                </Button>
                            </div>
                            <div className="mt-10 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-zinc-600">
                                <Check className="w-4 h-4 text-emerald-deep" /> eSIM — activate in minutes
                                <Check className="w-4 h-4 text-emerald-deep" /> Keep your number
                                <Check className="w-4 h-4 text-emerald-deep" /> Cancel anytime
                            </div>
                            <p className="mt-6 text-[11px] text-zinc-500 max-w-xl leading-relaxed" data-testid="sms-consent-disclaimer">
                                By signing up, you agree to receive transactional SMS from Flex Line. Msg &amp; data rates may apply. Reply STOP to opt out, HELP for help. See our{" "}
                                <a href="/privacy" className="text-emerald-deep link-underline">Privacy Policy</a> and{" "}
                                <a href="/terms" className="text-emerald-deep link-underline">Terms</a>.
                            </p>
                        </div>

                        {/* Hero image */}
                        <div className="lg:col-span-5 relative">
                            <img src={HERO_IMG} alt="FlexLine Mobile eSIM on a smartphone" data-testid="hero-image"
                                className="w-full rounded-2xl border border-[#DDD6FE] object-cover aspect-[4/5] lg:aspect-[3/4] shadow-[0_40px_90px_-40px_rgba(76,29,149,0.7)]" />
                            <div className="absolute -bottom-6 -left-6 bg-white border border-[#DDD6FE] rounded-2xl shadow-lg p-4 hidden md:flex items-center gap-3" data-testid="hero-esim-badge">
                                <div className="w-10 h-10 rounded-full bg-[#059669] grid place-items-center"><Zap className="w-5 h-5 text-white" /></div>
                                <div><p className="text-[10px] font-mono uppercase text-zinc-500">eSIM status</p><p className="text-sm font-medium">Active in minutes — automatically</p></div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Marquee numbers */}
                <div className="border-t border-[#DDD6FE] bg-paper/60 overflow-hidden">
                    <div className="flex whitespace-nowrap marquee py-4">
                        {[...tickerNumbers, ...tickerNumbers, ...tickerNumbers].map((n, i) => (
                            <span key={i} className="font-mono text-sm text-zinc-600 mx-10">
                                {n}
                            </span>
                        ))}
                    </div>
                </div>
            </section>

            {/* Wireless plans */}
            <section id="wireless" className="border-b border-[#DDD6FE]" data-testid="landing-wireless">
                <div className="max-w-7xl mx-auto px-6 lg:px-12 py-20">
                    <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-10">
                        <div>
                            <p className="font-mono text-[11px] uppercase tracking-wider text-emerald-deep mb-3">— FlexLine Mobile · Wireless plans</p>
                            <h2 className="font-display text-3xl sm:text-4xl font-light tracking-tight">
                                Pick your data. <span className="font-serif-italic text-[#4C1D95]">Talk &amp; text are unlimited.</span>
                            </h2>
                        </div>
                        <Button onClick={() => navigate("/wireless")} data-testid="wireless-see-all-btn"
                            className="bg-[#4C1D95] text-white hover:bg-[#2E1065] rounded-full h-11 px-6 shrink-0">
                            Plan details &amp; fees <ArrowUpRight className="w-4 h-4 ml-2" />
                        </Button>
                    </div>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
                        {mobilePlans.map((p) => <WirelessPlanCard key={p.id} p={p} fees={mobileFees} onChoose={choosePlan} />)}
                    </div>
                </div>
            </section>


            {/* Use cases */}
            <section className="border-b border-[#DDD6FE]">
                <div className="max-w-7xl mx-auto px-6 lg:px-12 py-16">
                    <div className="grid md:grid-cols-12 gap-8 items-center">
                        <p className="md:col-span-3 font-mono text-[11px] uppercase tracking-wider text-zinc-500">
                            What you get with FlexLine Mobile
                        </p>
                        <div className="md:col-span-9 grid grid-cols-2 md:grid-cols-4 gap-4">
                            {useCases.map(({ icon: Icon, label }) => (
                                <div key={label} className="flex items-center gap-3 px-4 py-3 border border-[#DDD6FE] rounded-sm bg-paper">
                                    <Icon className="w-4 h-4 text-emerald-deep" />
                                    <span className="text-sm font-medium">{label}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            <FeaturedDeals />

            {/* Two products, side by side */}
            <section id="products" className="border-b border-[#DDD6FE] bg-white" data-testid="landing-products">
                <div className="max-w-7xl mx-auto px-6 lg:px-12 py-20">
                    <p className="font-mono text-[11px] uppercase tracking-wider text-zinc-500 mb-3">— Two products. Pick what you need.</p>
                    <h2 className="font-display text-3xl sm:text-4xl font-light tracking-tight">Know exactly <span className="font-serif-italic text-[#4C1D95]">what you're getting.</span></h2>
                    <div className="mt-10 grid md:grid-cols-2 gap-6">
                        <ProductCard testid="product-wireless" tone="purple" tag="Product 01 · Wireless service" title="Wireless plans"
                            price="From $19.99/mo" href="/wireless" cta="See wireless plans"
                            lead="A full cell phone plan on eSIM — replaces your current carrier."
                            items={["Unlimited talk & text from your phone's own dialer", "1GB, 3GB, 5GB or Pay-As-You-Go 5G/LTE data", "Keep your number — transfer handled for you", "eSIM issued automatically after payment", "+ $3.99/mo regulatory fee · $15 activation"]} />
                        <ProductCard testid="product-second-number" tone="gold" tag="Product 02 · App-based line" title="Second phone number"
                            price="From $7.99/mo" href="/second-number" cta="See second-number plans"
                            lead="An extra private number on the phone you already have — not a cell plan."
                            items={["A real US number in the area code you choose", "Texting & calling inside the FlexLine app", "Works over Wi-Fi or your current data", "Voicemail, multiple numbers, no SIM swap", "Keeps your personal number private"]} />
                    </div>
                </div>
            </section>

            {/* How */}
            <section id="how" className="border-b border-[#DDD6FE] bg-paper/40">
                <div className="max-w-7xl mx-auto px-6 lg:px-12 py-24">
                    <div className="grid lg:grid-cols-12 gap-12 mb-12">
                        <div className="lg:col-span-6">
                            <p className="font-mono text-[11px] uppercase tracking-wider text-zinc-500 mb-4">
                                — How it works
                            </p>
                            <h2 className="font-display text-5xl md:text-6xl font-light leading-[0.95] tracking-tight">
                                Three steps.
                                <br />
                                <span className="font-serif-italic font-normal">Under a minute.</span>
                            </h2>
                        </div>
                    </div>
                    <div className="grid lg:grid-cols-3 gap-px bg-[#DDD6FE] border border-[#DDD6FE]">
                        {[
                            { n: "01", t: "Sign in with Google", d: "One click. We never see your password." },
                            { n: "02", t: "Pick a plan & number", d: "Choose a wireless plan, keep your number or grab a new one." },
                            { n: "03", t: "Talk & text", d: "Calls and texts ring right on your phone — with push alerts, even when the app is closed." },
                        ].map((step) => (
                            <div key={step.n} className="bg-paper p-12">
                                <span className="font-mono text-[11px] text-emerald-deep">{step.n}</span>
                                <h4 className="font-display text-3xl font-light mt-6 tracking-tight">{step.t}</h4>
                                <p className="text-base text-zinc-600 mt-4 leading-relaxed">{step.d}</p>
                            </div>
                        ))}
                    </div>
                    <div className="mt-12 text-center">
                        <Button
                            onClick={() => (user ? onCTA() : navigate("/wireless"))}
                            data-testid="how-signup-btn"
                            className="bg-[#4C1D95] hover:bg-[#2E1065] text-white rounded-full h-12 px-8 text-base"
                        >
                            {user ? "Go to dashboard" : "Choose a wireless plan"}
                        </Button>
                        <p className="text-sm text-zinc-500 mt-3">Paid upfront · No contract · Activated automatically</p>
                    </div>
                </div>
            </section>


            {/* Franchise opportunity */}
            <section className="border-b border-[#DDD6FE] bg-[#4C1D95] text-white" data-testid="landing-franchise">
                <div className="max-w-7xl mx-auto px-6 lg:px-12 py-16">
                    <div className="grid lg:grid-cols-12 gap-8 items-center">
                        <div className="lg:col-span-8">
                            <div className="inline-flex items-center gap-2 mb-5 bg-white/10 rounded-sm px-3 py-1 text-[11px] font-mono uppercase tracking-wider text-[#C4B5FD]">
                                <Building2 className="w-3.5 h-3.5" /> Authorized Dealer Program
                            </div>
                            <h3 className="font-display text-4xl md:text-5xl font-light leading-[0.98] tracking-tight">
                                Become an Authorized Dealer.
                                <br /><span className="font-serif-italic">Build a wireless business.</span>
                            </h3>
                            <p className="text-[#D8CCF5] mt-5 max-w-xl">
                                Launch your own FlexLine Mobile wireless business on our turnkey eSIM platform — recurring revenue,
                                full corporate support, and your own territory. No towers, no stores.
                            </p>
                        </div>
                        <div className="lg:col-span-4 flex lg:justify-end">
                            <Button
                                data-testid="landing-franchise-cta"
                                onClick={() => navigate("/franchise")}
                                className="bg-[#D97706] text-white hover:bg-[#B45309] rounded-sm h-12 px-7 text-base font-medium"
                            >
                                Become a dealer <ArrowUpRight className="w-4 h-4 ml-2" />
                            </Button>
                        </div>
                    </div>
                </div>
            </section>


            {/* Contact / Brand */}
            <section className="border-b border-[#DDD6FE] bg-paper/30">
                <div className="max-w-7xl mx-auto px-6 lg:px-12 py-16">
                    <div className="grid md:grid-cols-12 gap-10">
                        <div className="md:col-span-5">
                            <p className="font-mono text-[11px] uppercase tracking-wider text-zinc-500 mb-4">
                                — Get in touch
                            </p>
                            <h2 className="font-display text-4xl md:text-5xl font-light leading-[0.95] tracking-tight">
                                Real humans,
                                <br />
                                <span className="font-serif-italic font-normal">real answers.</span>
                            </h2>
                            <div className="mt-8 max-w-xs">
                                <QRBadge label="Scan to sign up on your phone" />
                            </div>
                        </div>
                        <div className="md:col-span-7 grid sm:grid-cols-2 gap-px bg-[#DDD6FE] border border-[#DDD6FE] self-start">
                            <ContactCard
                                label="Call or text us"
                                value="(877) 738-0943"
                                href="tel:+18777380943"
                                testid="contact-phone"
                            />
                            <ContactCard
                                label="Support"
                                value="info@flexlines.store"
                                href="mailto:info@flexlines.store"
                                testid="contact-email-support"
                            />
                            <ContactCard
                                label="Website"
                                value="www.flexlines.store"
                                href="https://www.flexlines.store"
                                testid="contact-website"
                            />
                            <ContactCard
                                label="Pricing & plans"
                                value="See our plans"
                                href="/wireless"
                                testid="contact-pricing"
                            />
                        </div>
                    </div>
                </div>
            </section>

            <SiteFooter />
        </div>
    );
}

function FeaturedDeals() {
    const [deals, setDeals] = React.useState([]);
    React.useEffect(() => {
        api.get("/deals").then(({ data }) => setDeals((data || []).slice(0, 3))).catch(() => {});
    }, []);
    if (deals.length === 0) return null;
    return (
        <section className="border-b border-[#DDD6FE] bg-paper/40" data-testid="landing-featured-deals">
            <div className="max-w-7xl mx-auto px-6 lg:px-12 py-16">
                <div className="flex items-end justify-between gap-4 mb-8">
                    <div>
                        <p className="font-mono text-[11px] uppercase tracking-wider text-emerald-deep mb-2 flex items-center gap-1.5">
                            <Tag className="w-3.5 h-3.5" /> Partner deals
                        </p>
                        <h2 className="font-display text-3xl sm:text-4xl font-light tracking-tight">
                            Perks from the <span className="font-serif-italic">Flex Line community.</span>
                        </h2>
                    </div>
                    <a href="/deals" className="text-sm font-mono text-emerald-deep link-underline shrink-0" data-testid="featured-deals-all-link">
                        See all deals →
                    </a>
                </div>
                <div className="grid md:grid-cols-3 gap-4">
                    {deals.map((d) => (
                        <a key={d.campaign_id} href={d.cta_url} target="_blank" rel="noopener noreferrer sponsored"
                            onClick={() => { if (d.campaign_id) api.post(`/ads/${d.campaign_id}/click`).catch(() => {}); }}
                            data-testid={`featured-deal-${d.campaign_id}`}
                            className="border border-[#DDD6FE] rounded-sm bg-paper p-5 hover:bg-[#F5F0FF] transition-colors relative">
                            <span className="absolute top-3 right-3 font-mono text-[9px] uppercase tracking-wider text-zinc-400">Sponsored</span>
                            <p className="font-mono text-[10px] uppercase tracking-wider text-zinc-500">{d.business_name}</p>
                            <h3 className="font-display text-lg mt-1">{d.headline}</h3>
                            <p className="text-sm text-zinc-600 mt-1">{d.body}</p>
                            <span className="inline-flex items-center gap-1 mt-3 text-sm font-medium text-emerald-deep">
                                {d.cta_label || "Learn more"} <ArrowRight className="w-3.5 h-3.5" />
                            </span>
                        </a>
                    ))}
                </div>
            </div>
        </section>
    );
}

function ContactCard({ label, value, href, testid }) {
    return (
        <a
            href={href}
            target={href.startsWith("http") ? "_blank" : undefined}
            rel={href.startsWith("http") ? "noopener noreferrer" : undefined}
            data-testid={testid}
            className="bg-paper p-6 hover:bg-[#F5F0FF] transition-colors group"
        >
            <p className="font-mono text-[10px] uppercase tracking-wider text-zinc-500 mb-3">{label}</p>
            <div className="font-mono text-base flex items-center justify-between gap-2">
                <span>{value}</span>
                <ArrowUpRight className="w-4 h-4 text-emerald-deep opacity-0 group-hover:opacity-100 transition-opacity" />
            </div>
        </a>
    );
}

function ProductCard({ testid, tone, tag, title, price, lead, items, href, cta }) {
    const purple = tone === "purple";
    return (
        <a href={href} data-testid={testid}
            className={`group rounded-2xl p-8 border flex flex-col transition-transform hover:-translate-y-1 ${purple ? "bg-[#4C1D95] text-white border-[#4C1D95]" : "bg-[#FFFBEB] border-[#FCD34D]"}`}>
            <p className={`font-mono text-[11px] uppercase tracking-wider ${purple ? "text-[#FCD34D]" : "text-[#B45309]"}`}>{tag}</p>
            <h3 className="font-display text-3xl mt-3">{title}</h3>
            <p className={`text-sm mt-2 ${purple ? "text-[#E9D5FF]" : "text-zinc-700"}`}>{lead}</p>
            <p className="font-display text-2xl mt-5">{price}</p>
            <ul className="mt-5 space-y-2 text-sm flex-1">
                {items.map((i) => <li key={i} className="flex gap-2"><Check className={`w-4 h-4 mt-0.5 shrink-0 ${purple ? "text-[#6EE7B7]" : "text-emerald-deep"}`} />{i}</li>)}
            </ul>
            <span className={`mt-6 inline-flex items-center gap-1 font-medium ${purple ? "text-[#FCD34D]" : "text-[#4C1D95]"}`}>{cta} <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" /></span>
        </a>
    );
}
